import { BOT, type PhysicsTuning } from '../config/tuning';
import { type CarState, maxSteerAngle } from '../sim/car';
import { clamp, datan, dcos, dsin } from '../sim/dmath';
import { type InputFrame, packInput, quantizeInput } from '../sim/input';
import type { TrackRuntime } from '../sim/trackQuery';
import { type SimEvent, spawnState, stepWorld } from '../sim/world';
import { accelTable, type RacingLine, speedProfile } from './racingLine';

/**
 * The bot driver: follows the racing line with pure pursuit and a speed
 * profile, through the same simulation and the same quantized inputs as a
 * player. Its lap proves the circuit is drivable and sets the medal times.
 */
export interface BotRun {
  gripLevel: number;
  completed: boolean;
  valid: boolean;
  lapMs: number;
  lapTicks: number;
  sectorMs: number[];
  /** Packed input frames from spawn to the end of the lap. */
  inputs: number[];
  endTick: number;
  barrierHits: number;
}

export interface BotPlan {
  line: RacingLine;
  profile: number[];
  gripLevel: number;
}

export function makePlan(line: RacingLine, p: PhysicsTuning, gripLevel: number): BotPlan {
  const profile = speedProfile(line, {
    mu: p.tireMu * Math.min(p.frontGrip, p.rearGrip) * gripLevel,
    // No later than the car's own brakes allow: the pre-war drums can't match modern discs.
    brakeDecel: Math.min(BOT.brakeDecel, p.brakeForce / p.mass) * gripLevel,
    accel: accelTable(p),
    vTop: 95,
    grade: line.grade,
    vcurv: line.vcurv,
    bank: line.bank,
    minLoad: line.minLoad,
    // Per-axle downforce relative to that axle's share of the weight; the weaker axle limits the corner.
    downforce: Math.min(
      (p.downforceFront * (p.cgToFront + p.cgToRear)) / (p.mass * p.cgToRear),
      (p.downforceRear * (p.cgToFront + p.cgToRear)) / (p.mass * p.cgToFront),
    ),
  });
  return { line, profile, gripLevel };
}

function wrapIndex(f: number, n: number): number {
  const r = f % n;
  return r < 0 ? r + n : r;
}

function sampleArr(arr: number[], f: number): number {
  const n = arr.length;
  const x = wrapIndex(f, n);
  const i = Math.floor(x);
  const j = i + 1 === n ? 0 : i + 1;
  const t = x - i;
  return arr[i] + (arr[j] - arr[i]) * t;
}

/** Fractional sample index of the car along the circuit. */
export function trackPosition(car: CarState, rt: TrackRuntime): number {
  const i = car.trackIndex;
  let ds = car.s - rt.s[i];
  if (ds < -rt.length * 0.5) ds += rt.length;
  const len = rt.segLen[i];
  return i + (len > 0 ? clamp(ds / len, 0, 1) : 0);
}

/**
 * The bot's inputs for this tick. `slipFeedForward` adds the tyres' slip angles
 * to the pure-pursuit steering (the bot's own driving); switch it off to get
 * just the geometric intent, e.g. for a simulated player on digital keys.
 */
export function botInput(car: CarState, rt: TrackRuntime, plan: BotPlan, p: PhysicsTuning, slipFeedForward = true): InputFrame {
  const n = rt.n;
  const ds = rt.length / n;
  const speed = Math.sqrt(car.vx * car.vx + car.vy * car.vy);
  const pos = trackPosition(car, rt);

  // Pure pursuit toward a point on the racing line ahead.
  const la = BOT.lookaheadBase + BOT.lookaheadPerSpeed * speed;
  const tgt = pos + la / ds;
  const txp = sampleArr(plan.line.x, tgt);
  const typ = sampleArr(plan.line.y, tgt);
  const ch = dcos(car.heading);
  const sh = dsin(car.heading);
  const dx = txp - car.x;
  const dy = typ - car.y;
  const lx = dx * ch + dy * sh;
  const ly = -dx * sh + dy * ch;
  const L = p.cgToFront + p.cgToRear;
  const curv = (2 * ly) / (lx * lx + ly * ly);
  // Pure pursuit gives the geometric angle; hard cornering also needs the
  // tyres' slip angles on top (front minus rear), or the bot runs wide.
  const slipFF = slipFeedForward ? clamp((car.slipRear - car.slipFront) * 0.8, -0.08, 0.08) : 0;
  const delta = datan(L * curv) + slipFF;
  const steer = clamp(delta / maxSteerAngle(p, speed), -1, 1);

  // Speed: the slowest target over a short horizon ahead.
  let vt = Infinity;
  const horizon = Math.max(2, Math.ceil((speed * 0.22) / ds));
  for (let k = 0; k <= horizon; k++) {
    const v = sampleArr(plan.profile, pos + k);
    if (v < vt) vt = v;
  }
  let throttle = 0;
  let brake = 0;
  if (speed > vt + 0.35) {
    brake = clamp((speed - vt) * BOT.brakeGain + 0.3, 0, 1);
    if (car.lockup > 0) brake *= 0.8;
  } else {
    throttle = clamp((vt - speed) * BOT.throttleGain + 0.62, 0, 1);
    if (car.wheelspin > 0) throttle *= 0.55;
    const rearSlip = Math.abs(car.slipRear) / p.peakSlipRear;
    if (rearSlip > 0.85) throttle *= 0.45;
    // Squeeze the throttle on exits: the harder it's cornering, the less.
    const latUse = Math.abs(car.ay) / (p.tireMu * 9.81);
    throttle *= clamp(1.45 - latUse, 0.3, 1);
  }
  return quantizeInput(throttle, brake, steer);
}

export function runBot(rt: TrackRuntime, p: PhysicsTuning, plan: BotPlan): BotRun {
  const state = spawnState(rt, p);
  const inputs: number[] = [];
  const events: SimEvent[] = [];
  let barrierHits = 0;
  for (let t = 0; t < BOT.maxTicks; t++) {
    const inp = botInput(state.car, rt, plan, p);
    inputs.push(packInput(inp));
    events.length = 0;
    stepWorld(state, inp, rt, p, events);
    for (const e of events) {
      if (e.type === 'barrier') barrierHits++;
      else if (e.type === 'lapComplete') {
        return {
          gripLevel: plan.gripLevel,
          completed: true,
          valid: e.valid && barrierHits === 0,
          lapMs: e.ms,
          lapTicks: e.ticks,
          sectorMs: e.sectorMs,
          inputs,
          endTick: e.endTick,
          barrierHits,
        };
      }
    }
  }
  return {
    gripLevel: plan.gripLevel,
    completed: false,
    valid: false,
    lapMs: Infinity,
    lapTicks: Infinity,
    sectorMs: [],
    inputs,
    endTick: state.tick,
    barrierHits,
  };
}

/**
 * Where the bot's tyres go: for every centreline sample, the furthest its outer
 * wheels reach on each side (metres from the centreline) over a lap at its
 * most committed grip level. A player on the limit puts a wheel over the white
 * line in the same places, so that's where the circuit gets runoff aprons.
 */
export function botReach(rt: TrackRuntime, p: PhysicsTuning, line: RacingLine): { left: Float64Array; right: Float64Array } {
  const plan = makePlan(line, p, BOT.gripLevels[BOT.gripLevels.length - 1]);
  const state = spawnState(rt, p);
  const left = new Float64Array(rt.n);
  const right = new Float64Array(rt.n);
  // The outer edge of a tyre, from the car's centre.
  const half = Math.max(p.halfTrackFront, p.halfTrackRear) + 0.15;
  const events: SimEvent[] = [];
  for (let t = 0; t < BOT.maxTicks; t++) {
    events.length = 0;
    stepWorld(state, botInput(state.car, rt, plan, p), rt, p, events);
    const c = state.car;
    const k = c.trackIndex;
    if (c.d + half > left[k]) left[k] = c.d + half;
    if (half - c.d > right[k]) right[k] = half - c.d;
    if (events.some((e) => e.type === 'lapComplete')) break;
  }
  return { left, right };
}

/** Try each grip level; the fastest clean lap is the author time. */
export function runBotSuite(rt: TrackRuntime, p: PhysicsTuning, line: RacingLine): { best: BotRun | null; runs: BotRun[] } {
  const runs: BotRun[] = [];
  let best: BotRun | null = null;
  for (const g of BOT.gripLevels) {
    const run = runBot(rt, p, makePlan(line, p, g));
    runs.push(run);
    if (run.valid && (!best || run.lapMs < best.lapMs)) best = run;
  }
  return { best, runs };
}
